/**
 * The open contract of each car (plate), from the Business Bay / Invygo contracts and the open
 * airport contracts - shared by "Is a car free?" and the Fleet Manager's Rented status.
 *
 *   openContractsByPlate(rows)  -> Map(PLATEKEY -> { no, customer, phone, branch, pick: Date, drop: Date })
 *   openContractFor(plate)      -> the same record for one plate (lists loaded once, kept 5 min)
 */
import { contractsService, airportService } from './firestore';

export const plateKey = (p) => String(p || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
const g = (row, ...keys) => { for (const k of keys) { const v = row && row[k]; if (v !== undefined && v !== null && String(v).trim() !== '') return v; } return ''; };

/** "dd/mm/yyyy[ HH:MM]" (Ejar) or an ISO text -> Date (local time) */
export const parseWhen = (s) => {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/.exec(String(s || '').trim());
    if (m) return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0));
    const d = s ? new Date(s) : null;
    return d && !isNaN(d.getTime()) ? d : null;
};

/** Plate -> its open contract (the latest pick-up wins; closed / delivered / cancelled ignored). */
export function openContractsByPlate(rows) {
    const out = new Map();
    (rows || []).forEach((r) => {
        if (/deliver|close|cancel/i.test(String(g(r, 'Status', 'status')))) return;
        const drop = parseWhen(g(r, 'Drop-off Date', 'dropoff_date', 'dropoffDate'));
        const pick = parseWhen(g(r, 'Pick-up Date', 'pickup_date', 'pickupDate'));
        const info = { no: g(r, 'Contract No.', 'contract_no', 'contractNo', 'id'), customer: g(r, 'Customer', 'customer'),
            phone: g(r, 'Phone Number', 'phone'), branch: g(r, 'Pick-up Branch', 'pickup_branch'), pick, drop };
        [g(r, 'Plate No.', 'plate_no', 'plateNo'), g(r, 'Invygo Plate', 'invygo_plate')].map(plateKey).filter(Boolean).forEach((k) => {
            const prev = out.get(k);
            if (!prev || (pick && (!prev.pick || pick > prev.pick))) out.set(k, info);
        });
    });
    return out;
}

const KEEP_MS = 5 * 60 * 1000;
let cache = null;          // { at, promise, rows, map }

/** Both contract lists, read once and kept a few minutes. A list that cannot be read rejects (and is not
 *  kept): a missing list would show a rented car as having no contract. */
export async function loadOpenContracts() {
    if (cache && Date.now() - cache.at < KEEP_MS) return cache.promise;
    const promise = Promise.all([contractsService.getAllContracts(), airportService.getOpenAirportContracts()])
        .then(([bb, ap]) => [...(bb || []), ...(ap || [])]);
    cache = { at: Date.now(), promise };
    promise.catch(() => { cache = null; });
    return promise;
}

export async function openContractFor(plate) {
    const rows = await loadOpenContracts();
    if (cache && (!cache.map || cache.rows !== rows)) { cache.map = openContractsByPlate(rows); cache.rows = rows; }
    return (cache && cache.map.get(plateKey(plate))) || null;
}
